import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarClock } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal, Kicker } from "../components/Reveal";
import { CALENDLY_URL, CAPITAL_MARKETS_SERVICES, FAQS } from "../data/content";
import { Faq } from "../components/Faq";

const CHALLENGES = [
  { title: "Access to Investors", body: "Broadening distribution beyond existing networks — reaching RIAs, retail broker-dealers, and institutions with a message that resonates." },
  { title: "Structure & Cost Discipline", body: "Choosing the right vehicle — Reg D, BDC, interval fund, or publicly registered LP — while streamlining costs that otherwise affect investor returns." },
  { title: "Diligence Infrastructure", body: "Offering documents, due-diligence processes, and portals that stand up to sophisticated intermediary review." },
  { title: "Compliant Marketing", body: "Investor presentations, webinars, and communication tools that engage potential investors and satisfy compliance review." },
];

const OUTCOMES = [
  "A sustainable business model for raising private capital",
  "A unique corporate brand and diversified sources of equity",
  "Sales teams equipped to communicate the offering clearly",
  "Time-tested introductions across the industry",
];

const CapitalMarkets = () => (
  <div data-testid="capital-markets-page">
    <PageHero
      kicker="Capital Markets Consulting"
      lines={[<span key="a">Raise capital</span>, <span key="b">with <em className="italic text-[#3E9BDC]">conviction.</em></span>]}
      intro="American Elm collaborates with fund managers to establish — or improve — a sustainable business model, build their unique corporate brand, and diversify their sources of equity while streamlining costs that may otherwise affect investor returns."
      meta={["Rule 506(b) Offerings", "Rule 506(c) Offerings", "Private-Fund Sponsors", "Market Participants"]}
    />

    <section className="py-20 md:py-28" data-testid="capital-challenges">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12">
        <Reveal>
          <Kicker>Sponsor Challenges</Kicker>
          <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl text-[#0A2B3D] tracking-tight max-w-3xl leading-[1.15]">
            Raising under Rule 506(b) or 506(c) is a distribution problem as much as a legal one.
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
          {CHALLENGES.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.06}>
              <div className="h-full p-8 md:p-10 bg-[#EBF3F8] border border-[#A9CFE2]/50" data-testid={`capital-challenge-${i}`}>
                <h3 className="font-serif font-bold text-xl text-[#0A2B3D]">{c.title}</h3>
                <p className="mt-3 text-sm text-[#475569] leading-relaxed">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 md:py-28 border-t border-[#0A2B3D]/10" data-testid="capital-capabilities">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12">
        <Reveal>
          <Kicker>Our Services</Kicker>
          <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl text-[#0A2B3D] tracking-tight max-w-2xl leading-[1.15]">
            From structure to distribution, one coordinated effort.
          </h2>
        </Reveal>
        <div className="mt-14 flex flex-col gap-5">
          {CAPITAL_MARKETS_SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.05}>
              <div
                className={`grid grid-cols-1 md:grid-cols-12 gap-6 p-8 md:p-12 bg-white border border-[#0A2B3D]/12 hover:border-[#3E9BDC]/60 transition-colors duration-500 ${i % 2 === 1 ? "md:ml-16" : ""}`}
                data-testid={`capital-service-${i}`}
              >
                <div className="md:col-span-4">
                  <h3 className="font-serif font-bold text-xl md:text-2xl text-[#0A2B3D]">{s.title}</h3>
                </div>
                <p className="md:col-span-8 text-[#475569] leading-relaxed">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-[#0A2B3D] text-white py-20 md:py-28" data-testid="capital-outcomes">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-7">
          <Kicker light>Strategic Outcomes</Kicker>
          <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl tracking-tight leading-[1.15]">
            What a coordinated capital strategy delivers.
          </h2>
          <ul className="mt-8 space-y-4">
            {OUTCOMES.map((o, i) => (
              <li key={o} className="flex gap-4 items-baseline border-b border-white/10 pb-4">
                <span className="text-white/85">{o}</span>
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal className="lg:col-span-5" delay={0.15}>
          <div className="border border-white/15 bg-[#082838] p-8 md:p-10 h-full" data-testid="affiliate-callout">
            <p className="eyebrow text-[#A9CFE2]">Our Affiliate</p>
            <h3 className="mt-4 font-serif font-bold text-2xl">American Elm Distribution Partners</h3>
            <p className="mt-4 text-sm text-white/65 leading-relaxed">
              In conjunction with our affiliate, American Elm Distribution Partners, we stand as a
              leading managing broker-dealer and versatile multi-product platform — connecting
              sponsors with the channels that move private offerings.
            </p>
            <a href="https://americanelmdp.com/" target="_blank" rel="noreferrer" data-testid="affiliate-link" className="mt-6 inline-flex items-center gap-2 text-sm text-[#3E9BDC] hover:text-white transition-colors">
              Visit American Elm DP <ArrowRight size={14} />
            </a>
          </div>
        </Reveal>
      </div>
    </section>

    <Faq items={FAQS.capital} id="capital-faq" />

    <div className="max-w-4xl mx-auto px-5 sm:px-8 -mt-6 md:-mt-10 pb-4" data-testid="capital-faq-explore-more">
      <Link to="/capital-markets-faq" data-testid="capital-explore-more-link" className="inline-flex items-center gap-2 text-sm font-semibold text-[#3E9BDC] hover:text-[#0A2B3D] transition-colors underline underline-offset-4">
        Explore more capital markets questions <ArrowRight size={15} />
      </Link>
    </div>

    <section className="bg-[#A9CFE2] py-16 md:py-24" data-testid="capital-cta">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
        <div>
          <h2 className="font-serif font-bold text-3xl sm:text-4xl text-[#0A2B3D] tracking-tight">Discuss your next offering.</h2>
          <p className="mt-3 text-[#0A2B3D]/75 max-w-xl">Whether you are structuring a first fund or scaling distribution, we can help.</p>
        </div>
        <div className="flex flex-col items-start md:items-end gap-3 shrink-0">
          <Link to="/contact?topic=Capital%20Markets%20Consulting" data-testid="capital-cta-button" className="btn-primary">
            Request a Consultation <ArrowRight size={16} />
          </Link>
          <a href={CALENDLY_URL} target="_blank" rel="noreferrer" data-testid="capital-calendly-link" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A2B3D] hover:text-[#082838] underline underline-offset-4">
            <CalendarClock size={16} /> Or schedule a 15-minute call
          </a>
        </div>
      </div>
    </section>
  </div>
);

export default CapitalMarkets;
